import { useVirtualizer } from '@tanstack/react-virtual';
import clsx, { ClassValue } from 'clsx';
import { first, isNil, last } from 'lodash';
import {
  AriaRole,
  ReactNode,
  Ref,
  RefCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEndReached } from './useEndReached';
import styles from './SectionList.module.css';

export interface SectionBase<ItemT> {
  data: ItemT[];
  key?: string;
}

export interface SectionListProps<ItemT, SectionT extends SectionBase<ItemT> = SectionBase<ItemT>> {
  sections: SectionT[];
  renderItem: (info: { item: ItemT; index: number; section: SectionT }) => ReactNode;
  renderSectionHeader?: (info: { section: SectionT }) => ReactNode;
  renderSectionFooter?: (info: { section: SectionT }) => ReactNode;
  /** Override section items (e.g. return [] when a section is collapsed). Defaults to section.data. */
  getSectionItems?: (section: SectionT) => ItemT[];
  /** When false, the section footer is omitted from the virtual list. Defaults to true. */
  shouldRenderSectionFooter?: (section: SectionT) => boolean;
  keyExtractor?: (item: ItemT, index: number) => string;
  /**
   * Pin section headers to the top of the scroll container as the user scrolls.
   * Virtualized headers are kept in the DOM via a pinned overlay when they scroll
   * out of the virtual window. Defaults to false.
   */
  stickySectionHeaders?: boolean;
  /** Pinned section headers cover the top chevron instead of sitting under it. Use
   * when header content spans the chevron's spot. Defaults to false. */
  stickyHeadersCoverIndicator?: boolean;
  ListHeaderComponent?: ReactNode;
  ListFooterComponent?: ReactNode;
  className?: ClassValue;
  /** ARIA role for the scroll container, e.g. "list" when items render as listitems. */
  role?: AriaRole;
  testID?: string;
  overscan?: number;
  /* Starting height for rows that haven't been measured yet. Only affects the
   * scrollbar estimate and how many rows render first; real heights take over
   * as they measure. Defaults to 48. */
  estimatedItemHeight?: number;
  /* Mask the overscroll edges and show chevrons. Defaults to false. */
  showScrollIndicators?: boolean;
  /* Mask the overscroll edges. Defaults to true if showScrollIndicators is
   * true. Pass only this prop to show a mask with no chevrons. */
  showScrollMask?: boolean;
  /* Infinite scroll. Fires once per approach, re-arming on scroll away or new
   * items; callers still need their own in-flight/exhausted guard. */
  onEndReached?: () => void;
  /** Trigger distance for onEndReached, as a multiple of list height. Defaults to 0.5. */
  onEndReachedThreshold?: number;
  /* Imperative scrolling handle. A plain prop (not forwardRef) so the component
   * keeps its generic signature. */
  listRef?: Ref<SectionListHandle>;
}

export interface SectionListHandle {
  /** Scroll the item at (sectionIndex, itemIndex) to the given edge of the viewport. */
  scrollToItem(options: {
    sectionIndex: number;
    itemIndex: number;
    align?: 'start' | 'center' | 'end' | 'auto';
  }): void;
}

type FlatEntry<ItemT, SectionT extends SectionBase<ItemT>> =
  | { type: 'header'; section: SectionT; sectionIndex: number }
  | { type: 'item'; item: ItemT; section: SectionT; itemIndex: number; sectionIndex: number }
  | { type: 'footer'; section: SectionT; sectionIndex: number };

const ESTIMATED_ITEM_HEIGHT = 48;
const OVERSCAN = 5;
// Fraction of the visible list height
const END_REACHED_THRESHOLD = 0.5;

export function SectionList<ItemT, SectionT extends SectionBase<ItemT> = SectionBase<ItemT>>({
  sections,
  renderItem,
  renderSectionHeader,
  renderSectionFooter,
  getSectionItems,
  shouldRenderSectionFooter,
  keyExtractor,
  stickySectionHeaders = false,
  stickyHeadersCoverIndicator = false,
  ListHeaderComponent,
  ListFooterComponent,
  className,
  role,
  testID,
  overscan = OVERSCAN,
  estimatedItemHeight = ESTIMATED_ITEM_HEIGHT,
  onEndReached,
  onEndReachedThreshold = END_REACHED_THRESHOLD,
  showScrollIndicators = false,
  showScrollMask = showScrollIndicators,
  listRef,
}: SectionListProps<ItemT, SectionT>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Track the header height so the virtualizer can account for it via scrollMargin.
  // Without this, TanStack Virtual's offset calculations ignore the non-virtualized header.
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (isNil(el)) {
      return;
    }
    // Synchronous initial measurement before paint so the virtualizer never starts
    // with scrollMargin=0 when a header is present. ResizeObserver handles updates.
    setScrollMargin(el.offsetHeight);
    const observer = new ResizeObserver(() => {
      setScrollMargin(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Expose the scrollbar's layout width as a CSS custom property so children
  // can use a fixed `calc(var(--rail-width) - var(--scrollbar-width, 0px))`
  // instead of `width: 100%`, which avoids reflow during collapse animations.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (isNil(el)) {
      return;
    }
    const update = () => {
      el.style.setProperty('--scrollbar-width', `${el.offsetWidth - el.clientWidth}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el, { box: 'content-box' });
    return () => observer.disconnect();
  }, []);

  const flatItems = useMemo((): FlatEntry<ItemT, SectionT>[] => {
    const result: FlatEntry<ItemT, SectionT>[] = [];
    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
      const section = sections[sectionIndex];
      if (renderSectionHeader) {
        result.push({ type: 'header', section, sectionIndex });
      }
      const sectionItems = getSectionItems ? getSectionItems(section) : section.data;
      for (let itemIndex = 0; itemIndex < sectionItems.length; itemIndex++) {
        result.push({
          type: 'item',
          item: sectionItems[itemIndex],
          section,
          itemIndex,
          sectionIndex,
        });
      }
      if (renderSectionFooter && (shouldRenderSectionFooter?.(section) ?? true)) {
        result.push({ type: 'footer', section, sectionIndex });
      }
    }
    return result;
  }, [
    sections,
    renderSectionHeader,
    renderSectionFooter,
    getSectionItems,
    shouldRenderSectionFooter,
  ]);

  useEndReached({
    scrollRef,
    onEndReached,
    onEndReachedThreshold,
    itemCount: flatItems.length,
  });

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => scrollRef.current,
    // Headers/footers keep the smaller default; only rows take the caller's estimate
    estimateSize: index =>
      flatItems[index].type === 'item' ? estimatedItemHeight : ESTIMATED_ITEM_HEIGHT,
    overscan,
    scrollMargin,
    getItemKey: index => {
      const entry = flatItems[index];
      if (entry.type === 'header') {
        return !isNil(entry.section.key)
          ? `header-${entry.section.key}`
          : `header-${entry.sectionIndex}`;
      }
      if (entry.type === 'footer') {
        return !isNil(entry.section.key)
          ? `footer-${entry.section.key}`
          : `footer-${entry.sectionIndex}`;
      }
      const sectionKey = !isNil(entry.section.key) ? entry.section.key : String(entry.sectionIndex);
      const itemKey = keyExtractor
        ? keyExtractor(entry.item, entry.itemIndex)
        : String(entry.itemIndex);
      // Namespace by section and index so the same item id in multiple sections (e.g.
      // grouped cards) or duplicated rows in one section do not collide.
      return `item-${sectionKey}-${itemKey}-${entry.itemIndex}`;
    },
  });

  useImperativeHandle(
    listRef,
    () => ({
      scrollToItem: ({ sectionIndex, itemIndex, align = 'start' }) => {
        const flatIndex = flatItems.findIndex(
          entry =>
            entry.type === 'item' &&
            entry.sectionIndex === sectionIndex &&
            entry.itemIndex === itemIndex,
        );
        if (flatIndex >= 0) {
          virtualizer.scrollToIndex(flatIndex, { align });
        }
      },
    }),
    [flatItems, virtualizer],
  );

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const headerIndices = useMemo(() => {
    const indices: number[] = [];
    for (let index = 0; index < flatItems.length; index++) {
      if (flatItems[index].type === 'header') {
        indices.push(index);
      }
    }
    return indices;
  }, [flatItems]);

  // Derive the active section from the first visible virtual row, not scroll offsets.
  // getOffsetForIndex can return stale zeros before measurement, which incorrectly
  // pins the last section header when a large section expands.
  const activeHeaderIndex = useMemo((): number | null => {
    if (!stickySectionHeaders || headerIndices.length === 0) {
      return null;
    }
    const firstVisibleIndex = first(virtualItems)?.index;
    if (isNil(firstVisibleIndex)) {
      return headerIndices[0];
    }
    for (let index = firstVisibleIndex; index >= 0; index--) {
      const entry = flatItems[index];
      if (entry?.type === 'header') {
        return index;
      }
    }
    return headerIndices[0];
  }, [stickySectionHeaders, headerIndices, virtualItems, flatItems]);

  const isNaturalHeaderVisible =
    !isNil(activeHeaderIndex) && virtualItems.some(item => item.index === activeHeaderIndex);

  const pinnedHeaderEntry =
    stickySectionHeaders &&
    !isNil(activeHeaderIndex) &&
    !isNaturalHeaderVisible &&
    flatItems[activeHeaderIndex]?.type === 'header'
      ? flatItems[activeHeaderIndex]
      : null;

  // item.start / item.end include scrollMargin; getTotalSize() does not.
  // paddingTop = height of items above first visible = firstVisible.start - scrollMargin
  // paddingBottom = height of items below last visible = totalSize - (lastVisible.end - scrollMargin)
  const paddingTop = Math.max(0, (first(virtualItems)?.start ?? scrollMargin) - scrollMargin);
  const paddingBottom = Math.max(
    0,
    totalSize - ((last(virtualItems)?.end ?? scrollMargin) - scrollMargin),
  );

  return (
    <div
      ref={scrollRef}
      className={clsx(
        styles.sectionList,
        showScrollMask && styles.showScrollMask,
        showScrollIndicators && styles.showScrollIndicators,
        stickyHeadersCoverIndicator && styles.stickyHeadersCoverIndicator,
        className,
      )}
      role={role}
      data-testid={testID}
    >
      {showScrollMask && <div className={clsx(styles.scrollMask, styles.top)} aria-hidden />}
      {showScrollIndicators && (
        <div className={clsx(styles.scrollIndicator, styles.top)} aria-hidden />
      )}
      {ListHeaderComponent && <div ref={headerRef}>{ListHeaderComponent}</div>}
      {pinnedHeaderEntry?.type === 'header' && (
        <div className={styles.stickyHeader}>
          {renderSectionHeader?.({ section: pinnedHeaderEntry.section }) ?? null}
        </div>
      )}
      {paddingTop > 0 && <div style={{ height: paddingTop }} aria-hidden />}
      {virtualItems.map(virtualItem => (
        <SectionListItem
          key={virtualItem.key}
          virtualItem={virtualItem}
          entry={flatItems[virtualItem.index]}
          measureElement={virtualizer.measureElement}
          stickySectionHeaders={stickySectionHeaders}
          renderSectionHeader={renderSectionHeader}
          renderItem={renderItem}
          renderSectionFooter={renderSectionFooter}
        />
      ))}
      {paddingBottom > 0 && <div style={{ height: paddingBottom }} aria-hidden />}
      {ListFooterComponent && <div>{ListFooterComponent}</div>}
      {showScrollMask && <div className={clsx(styles.scrollMask, styles.bottom)} aria-hidden />}
      {showScrollIndicators && (
        <div className={clsx(styles.scrollIndicator, styles.bottom)} aria-hidden />
      )}
    </div>
  );
}

interface SectionListItemProps<ItemT, SectionT extends SectionBase<ItemT>> {
  virtualItem: { index: number };
  entry: FlatEntry<ItemT, SectionT>;
  measureElement: RefCallback<Element>;
  stickySectionHeaders: boolean;
  renderSectionHeader?: (info: { section: SectionT }) => ReactNode;
  renderItem: (info: { item: ItemT; index: number; section: SectionT }) => ReactNode;
  renderSectionFooter?: (info: { section: SectionT }) => ReactNode;
}

function SectionListItem<ItemT, SectionT extends SectionBase<ItemT>>({
  virtualItem,
  entry,
  measureElement,
  stickySectionHeaders,
  renderSectionHeader,
  renderItem,
  renderSectionFooter,
}: SectionListItemProps<ItemT, SectionT>) {
  const sectionKey = entry.section.key ?? String(entry.sectionIndex);
  const isHeader = entry.type === 'header';
  const isItem = entry.type === 'item';
  const isFooter = entry.type === 'footer';

  return (
    <div
      ref={measureElement}
      data-index={virtualItem.index}
      data-section-key={sectionKey}
      className={clsx(isHeader && stickySectionHeaders && styles.stickyHeader)}
    >
      {isHeader && renderSectionHeader?.({ section: entry.section })}
      {isItem && renderItem({ item: entry.item, index: entry.itemIndex, section: entry.section })}
      {isFooter && renderSectionFooter?.({ section: entry.section })}
    </div>
  );
}
